/**
 * Render helpers for audit before/after JSON (TMMAPP-0022). Top-level keys become rows;
 * nested objects go one level deep, then `mono` JSON. Numbers ending in `Minor` format as
 * money; a sibling `currency` key wins over the page currency.
 */
import { formatCivilDate, formatInstant, formatMoneyMinor } from '@/i18n/format';

const CIVIL = /^\d{4}-\d{2}-\d{2}$/;

export interface FormatCtx {
  locale: string;
  timeZone: string;
  currency: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function siblingCurrency(record: Record<string, unknown>, fallback: string | null): string | null {
  return typeof record.currency === 'string' && record.currency.length > 0
    ? record.currency
    : fallback;
}

export function formatScalar(
  key: string,
  value: unknown,
  ctx: FormatCtx,
  currency: string | null,
): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && key.endsWith('Minor') && Number.isFinite(value)) {
    return formatMoneyMinor(value, currency, ctx.locale);
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') {
    if (CIVIL.test(value)) {
      try {
        return formatCivilDate(value, ctx.locale);
      } catch {
        return value;
      }
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      try {
        return formatInstant(value, ctx.locale, ctx.timeZone);
      } catch {
        return value;
      }
    }
    return value;
  }
  return JSON.stringify(value);
}

export function formatNested(value: unknown, ctx: FormatCtx, currency: string | null): string {
  if (Array.isArray(value)) {
    return value.map((item) => formatScalar('', item, ctx, currency)).join(', ');
  }
  if (isRecord(value)) {
    const innerCurrency = siblingCurrency(value, currency);
    return Object.entries(value)
      .map(([key, child]) => {
        if (isRecord(child) || (Array.isArray(child) && child.some((item) => isRecord(item)))) {
          return `${key}: ${JSON.stringify(child)}`;
        }
        return `${key}: ${formatScalar(key, child, ctx, innerCurrency)}`;
      })
      .join(', ');
  }
  return formatScalar('', value, ctx, currency);
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? value : null;
}

export function changedKeys(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): ReadonlySet<string> {
  const keys = new Set<string>([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
  const changed = new Set<string>();
  for (const key of keys) {
    if (JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key])) changed.add(key);
  }
  return changed;
}
